/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useSelector } from "react-redux";
import { setScheduledTask } from "../../../store/api/tasks/reducer";
import { tasksSelector } from "../../../store/api/tasks/selector";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { constStr } from "../../../globals/constants";
import { resetSelfConfig } from "../helpers";
import { RadixSelectControl } from "../../ui/radix-select";
import { Label } from "../../ui/label";
import { InputField } from "../../Shared/Radix/Inputs/InputField";
import { Checkbox } from "../../ui/checkbox";

export const LTESelfConfiguration = () => {
    const dispatch = useStoreDispatch();
    const errorMsg = useSelector(errorMsgSelector);
    const scheduledtasks = useSelector(tasksSelector);

    const onInputChange = (name: string, value: string) => {
        if (!["lteSelfConfigType", "txPowerType", "allString", "zoneRemValue", "targetRSRP", "targetSIR"].includes(name) && value.match(/[^0-9]/)) {
            return;
        }
        dispatch(setScheduledTask({ [name]: value }));
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const onRadioChange = (value: string) => {
        if (value === constStr.neighborList) {
            dispatch(setScheduledTask({ ...resetSelfConfig, lteSelfConfigType: value, txPowerType: "Max Power" }));
        } else {
            dispatch(setScheduledTask({ ...resetSelfConfig, lteSelfConfigType: value }));
        }
        dispatch(setErrorMsg({ ...errorMsg, lteSelfConfigType: "" }));
    };

    const onSelectChange = (value: string) => {
        dispatch(setScheduledTask({ ...resetSelfConfig, txPowerType: value }));
        dispatch(setErrorMsg({ ...errorMsg, txPowerType: "" }));
    };

    const onCheckboxChange = (checked: boolean) => {
        dispatch(setScheduledTask({ allString: checked ? "ALL" : "", zoneIdValue: "" }));
    };

    const isAllSelect = scheduledtasks?.allString === "ALL";
    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">LTE Self Configuration Options</p>
                <div className="flex flex-col gap-2">
                    {[
                        { value: constStr.neighborList, label: "Neighbor List Create" },
                        { value: constStr.txPower, label: "TX Power Assignment" },
                    ].map((opt) => (
                        <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm">
                            <input
                                type="radio"
                                name="lteSelfConfigType"
                                value={opt.value}
                                checked={scheduledtasks?.lteSelfConfigType === opt.value}
                                onChange={() => onRadioChange(opt.value)}
                                style={{ accentColor: "var(--primary)" }}
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            </div>
            {scheduledtasks?.lteSelfConfigType === constStr.txPower && (
                <>
                    <div className="space-y-2">
                        <Label>TX Power Assignment Type</Label>
                        <RadixSelectControl
                            options={[
                                { value: "Max Power", label: "Max Power" },
                                { value: "REM Based", label: "REM Based" },
                            ]}
                            value={scheduledtasks.txPowerType || "Max Power"}
                            placeholder="__placeholder__"
                            onValueChange={onSelectChange}
                        />
                    </div>

                    {scheduledtasks.txPowerType !== "REM Based" ? (
                        <>
                            <div>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox checked={scheduledtasks?.allString === "ALL"} onCheckedChange={onCheckboxChange} />
                                    All
                                </label>
                            </div>
                            <div>
                                <InputField
                                    label="Zone"
                                    name="zoneIdValue"
                                    value={scheduledtasks?.zoneIdValue ?? ""}
                                    disabled={isAllSelect}
                                    onChange={onInputChange}
                                    placeholder="Enter zone"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div>
                                <p className="text-sm font-medium">REM Based Optional Parameters</p>
                            </div>
                            <div>
                                <InputField
                                    label="Number of neighbor To Cover"
                                    name="neighborToCover"
                                    placeholder="Enter number of neighbor to cover"
                                    value={scheduledtasks?.neighborToCover ?? ""}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Max Swing"
                                    name="maxSwing"
                                    placeholder="Enter max swing"
                                    value={scheduledtasks?.maxSwing ?? ""}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Target RSRP"
                                    name="targetRSRP"
                                    value={scheduledtasks?.targetRSRP ?? ""}
                                    type="number"
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Target SIR"
                                    name="targetSIR"
                                    value={scheduledtasks?.targetSIR ?? ""}
                                    type="number"
                                    onChange={onInputChange}
                                    placeholder="Enter target sir"
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Nominal PL High"
                                    name="nominalPLHigh"
                                    placeholder="Enter nominal pl high"
                                    value={scheduledtasks?.nominalPLHigh ?? ""}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Nominal PL Low"
                                    name="nominalPLLow"
                                    placeholder="Enter nominal pl low"
                                    value={scheduledtasks?.nominalPLLow ?? ""}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div>
                                <InputField
                                    label="Zones"
                                    name="zoneRemValue"
                                    value={scheduledtasks?.zoneRemValue ?? ""}
                                    onChange={onInputChange}
                                    placeholder="Enter zones"
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
};